// Grafana Faro browser RUM — client-side real-user monitoring that posts page
// loads, uncaught errors and Web Vitals to the one production collector
// (`prod-alloy`, the Alloy OTLP/Faro receiver; canon 85 §3, canon 93 §2). It is
// the browser half of the observability story whose Go side is internal/obs;
// this module is the only place the SDK is touched.
//
// Three constraints shape it (and every one is a test or a visible guard):
//
//   1. OFF / NO-OP WHEN UNCONFIGURED. Faro initialises ONLY when the server
//      rendered a collector URL into the page. The server emits
//      `<meta name="objexiv:faro-url" content="…">` only when
//      OBJEXIV_FARO_COLLECTOR_URL is set (cmd/server → render.Page); with no
//      meta tag this module returns immediately and nothing is loaded, measured
//      or sent. Dev and test never phone home because the var is unset there.
//
//   2. NO THIRD PARTY AT REQUEST TIME. The SDK is bundled INTO island.js by the
//      one esbuild step and served from our own origin, exactly like the
//      self-hosted fonts (canon 94 §6). There is no runtime CDN <script>. The
//      collector URL is our own Alloy endpoint, reached only when configured.
//
//   3. NO PII. We send telemetry, not people. No session/user identity is
//      attached, and Faro's default view/session tracking is left to anonymous
//      ids only — we never call faro.api.setUser(). The collected signals are
//      page timing, Web Vitals and error traces, not who the viewer is.
//
// The island stays framework-free (canon 91 §1): Faro is a small instrumentation
// library, not an SPA framework, and it is wired at the entry alongside the
// gesture island, not woven through it.

import { initializeFaro, getWebInstrumentations } from "@grafana/faro-web-sdk";

/** The meta name the server renders the collector URL under. Kept in one place
 *  so the island and the page template agree on the contract. */
const FARO_META = "objexiv:faro-url";

/** The app name Faro tags every signal with, so the collector can route the
 *  browser stream alongside the Go-side (internal/obs) service. */
const APP_NAME = "objexiv-field";

/** collectorURL reads the server-rendered collector endpoint from the page, or
 *  null when the server did not render one (the unconfigured / dev / test case).
 *  A blank or whitespace-only content is treated as absent, so a meta tag that
 *  rendered an empty value never arms RUM. */
export function collectorURL(doc: Document = document): string | null {
  const meta = doc.querySelector<HTMLMetaElement>(`meta[name="${FARO_META}"]`);
  const url = meta?.content?.trim();
  return url ? url : null;
}

let started = false;

/**
 * initFaro arms browser RUM IF AND ONLY IF the server rendered a collector URL.
 * It is safe to call unconditionally at startup: with no configured collector it
 * returns false and does nothing (no network, no measurement). It also guards
 * against a double-init (one Faro per document), returning false on a second
 * call. Returns true only when RUM was actually initialised this call.
 */
export function initFaro(doc: Document = document): boolean {
  if (started) return false;
  const url = collectorURL(doc);
  if (!url) return false; // unconfigured → no-op, the dev/test default

  initializeFaro({
    url,
    app: { name: APP_NAME },
    // Default web instrumentation: page-load and navigation timing, Web Vitals,
    // and uncaught errors/unhandled rejections. No PII is captured by these —
    // they are timings and error traces, not identity. We do not add the user
    // or session instrumentation with any personal attributes.
    instrumentations: [...getWebInstrumentations()],
  });

  started = true;
  return true;
}
